// Invitation redemption, invitee side (arc 101 slice 01).
//
// `invitationAcceptUrl` has minted `${DASHBOARD_URL}/invite?token=…` since the
// invitation object shipped (#2351) and this bundle had no `/invite` route and
// no caller for `POST /v1/invitations/accept` — so every invite mailed to an
// address that is not on the recipient's GitHub profile landed on `NotFound`.
// This module is the POST and the verdict; `lib/invite-copy.ts` is how a verdict
// READS, and `routes/Invite.tsx` is the screen.
//
// The wire is `apps/api/src/routes/invitations.ts`: a 2xx for the three "you
// have access" verdicts (discriminated by flags on the body), 410 for the two
// dead ones, 404 for an unknown token. The route rides `read:account` on
// purpose, so a WAITLISTED session can reach it — which is what makes this
// screen usable by the only people who need it.

import { ApiError, apiFetch, errorReason } from './api';
import { SESSION_TOKEN } from './session';

/**
 * The landing's own address — the path `invitationAcceptUrl` mints and
 * `TOP_ROUTE_TABLE` mounts.
 *
 * It lives HERE because this module already owns the redemption call, the
 * verdict and `inviteTokenFrom`, and because it has a second reader: the
 * waitlist gate exempts exactly this path (`lib/app-gate.ts`). Two spellings of
 * one path is how the exemption stops covering the route it was written for.
 */
export const INVITE_PATH = '/invite' as const;

/**
 * What the accept call settled to: the API's six verdicts, plus the two answers
 * that never reach the API — a link with no token on it, and a call that did not
 * complete.
 *
 * A refusal must be distinguishable from nothing-happened, so `failed` is a
 * member rather than a swallowed `null`: "we could not reach the server" and
 * "there is no such invitation" send a person to two different places.
 */
export type InviteOutcome =
  | { readonly kind: 'accepted'; readonly accountId: string | null; readonly role: string | null }
  | {
      readonly kind: 'already_a_member';
      readonly accountId: string | null;
      readonly role: string | null;
    }
  | {
      readonly kind: 'already_accepted';
      readonly acceptedAt: string | null;
      readonly bySelf: boolean;
    }
  /**
   * Recorded, NOT active (plan 102 slice 05): a platform invitation whose promised
   * capacity could not be issued. The person stays waitlisted until an operator
   * funds them; `detail` is the server's sentence for them, shown as given.
   */
  | { readonly kind: 'pending_funding'; readonly detail: string }
  | { readonly kind: 'expired'; readonly at: string | null }
  | { readonly kind: 'revoked'; readonly at: string | null }
  /**
   * A 410 that did not say which. Reachable RIGHT NOW, not defensive padding:
   * the `reason` extension member ships in the same change as this file, and the
   * SPA is a separately-cached static bundle — so a new bundle talking to an API
   * pod that predates it gets a bare 410. Collapsing that into `expired` would
   * tell some people the wrong thing; collapsing it into `failed` would tell
   * them to retry a link that can never work again.
   */
  | { readonly kind: 'not_live' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'no_token' }
  | { readonly kind: 'failed'; readonly reason: string };

/**
 * The token off `?token=…`, or `null` when the link carries none.
 *
 * The SHAPE is not checked — that is the server's job, and it refuses a bad one
 * without ever saying what a good one looks like. A repeated param takes the
 * first value (`OnboardingReturnNotice`'s rule for the same `string | string[]`
 * router type); an empty one is no token at all.
 */
export function inviteTokenFrom(raw: string | string[] | undefined): string | null {
  const value = Array.isArray(raw) ? raw[0] : raw;
  const token = value?.trim() ?? '';
  return token === '' ? null : token;
}

/** The 2xx body of `POST /v1/invitations/accept`, as the route writes it. */
type AcceptBody = {
  readonly accepted?: boolean;
  readonly already_a_member?: boolean;
  readonly already_accepted?: boolean;
  readonly accepted_at?: string;
  readonly by_self?: boolean;
  readonly account_id?: string | null;
  readonly role?: string | null;
  /** `false` with `pending_funding` = recorded but not let in (plan 102 slice 05). */
  readonly approved?: boolean;
  readonly pending_funding?: boolean;
  readonly detail?: string;
};

/** Read a string extension member off a problem body, or `null`. */
function member(
  extensions: Readonly<Record<string, unknown>> | undefined,
  name: string,
): string | null {
  const value = extensions?.[name];
  return typeof value === 'string' ? value : null;
}

/**
 * Map a 2xx body to its verdict. The success verdicts differ by flag, and
 * `pending_funding` is checked FIRST: that body also says `accepted: true`, and
 * reading it as access would hand an unfunded, still-waitlisted person the funnel.
 */
export function outcomeFromBody(body: AcceptBody): InviteOutcome {
  if (body.pending_funding === true || body.approved === false) {
    return { kind: 'pending_funding', detail: body.detail ?? '' };
  }
  if (body.already_a_member === true) {
    return {
      kind: 'already_a_member',
      accountId: body.account_id ?? null,
      role: body.role ?? null,
    };
  }
  if (body.already_accepted === true) {
    return {
      kind: 'already_accepted',
      acceptedAt: body.accepted_at ?? null,
      // Absent reads as "someone else": the honest default on an unknown is the
      // one that tells a person to look, not the one that tells them to relax.
      bySelf: body.by_self === true,
    };
  }
  return { kind: 'accepted', accountId: body.account_id ?? null, role: body.role ?? null };
}

/**
 * Map a thrown call to its verdict. Status first, then the `reason` extension —
 * never the `detail` prose, which is a sentence for a human and may be reworded
 * without notice. Both 410s share a status AND a problem type, so `reason` is
 * the only machine answer to which of them happened.
 */
export function outcomeFromError(error: unknown): InviteOutcome {
  if (!(error instanceof ApiError)) {
    return { kind: 'failed', reason: errorReason(error, 'request failed') };
  }
  if (error.status === 404) return { kind: 'not_found' };
  if (error.status === 410) {
    switch (member(error.extensions, 'reason')) {
      case 'expired':
        return { kind: 'expired', at: member(error.extensions, 'expires_at') };
      case 'revoked':
        return { kind: 'revoked', at: member(error.extensions, 'revoked_at') };
      default:
        return { kind: 'not_live' };
    }
  }
  return { kind: 'failed', reason: errorReason(error, error.message) };
}

/**
 * Redeem `token` for the signed-in identity. Never throws: every way this can
 * end is a verdict the screen has copy for, and a raw `ApiError` shown to an
 * invitee would be the worst first page in the product.
 *
 * `session` is the API credential (the cookie marker or a dev PAT) — NOT the
 * invitation token, which is the first argument.
 */
export async function acceptInvitation(
  token: string,
  session: string | null = SESSION_TOKEN,
): Promise<InviteOutcome> {
  try {
    const body = await apiFetch<AcceptBody>('/v1/invitations/accept', session, {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    return outcomeFromBody(body);
  } catch (error) {
    return outcomeFromError(error);
  }
}
